import { useMemo, useState } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'
import firebase from 'firebase'

import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Radio,
    Grid,
    Tooltip,
    makeStyles
} from '@material-ui/core'

import { useBoolean } from '@guidde-co/shared.hooks.use-boolean'

import { logToAnalytics } from 'modules'
import { useDataMutation, useNotification, useAuth, getRoleName, ROLES_LIST, ROLE } from 'hooks'
import { paths } from 'app/paths'
import { RowUserType } from 'UI/Routes/account-settings/tabs'

const pendingUsersCollectionRef = (orgId: string) =>
    firebase.firestore().collection(`usersPerOrg/${orgId}/pendingUsers/`)

type Props = {
    onClose: () => void
    user: RowUserType
    isRoleUpgradeDisabled: boolean
}

const useStyles = makeStyles(({ zIndex }) => ({
    popper: {
        zIndex: zIndex.modal + 1
    }
}))

export const PermissionsDialog = ({ onClose, user, isRoleUpgradeDisabled }: Props) => {
    const params = useParams<{ orgId: string }>()

    const loading = useBoolean()
    const classes = useStyles()

    const { orgId } = useAuth()
    const isOrgSettingsRoute = useRouteMatch(paths.organizationSettings)?.isExact ?? false
    const organizationId = isOrgSettingsRoute ? params.orgId : orgId

    const roleOption = useMemo(() => {
        if (Boolean(user.sentAt)) {
            return getRoleName(user.permissions || ROLE.VIEWER)
        }
        return ROLES_LIST.find(role => role.name === user.roles)
    }, [user])

    const [option, setOption] = useState(roleOption)

    const { showSuccessNotification, showErrorNotification } = useNotification()

    const $setUserPermissions = useDataMutation('/c/v1/user', 'POST', {
        onSuccess: () => {
            showSuccessNotification('User permissions have been updated')
            onClose()
            loading.setFalse()
            logToAnalytics('change_user_permissions', {
                user,
                permissions: option?.name
            })
        }
    })

    const setPendingUserPermissions = () => {
        if (!option) return

        pendingUsersCollectionRef(organizationId)
            .doc(user.email)
            .update({
                permissions: option.level
            })
            .then(() => {
                showSuccessNotification('User permissions have been updated')
                logToAnalytics('change_pending_user_permissions', {
                    user,
                    permissions: option.name
                })
            })
            .catch(e => {
                showErrorNotification('Something went wrong. Please try again later')
                console.error(e)
            })
            .finally(() => {
                loading.setFalse()
                onClose()
            })
    }

    const handleSave = () => {
        if (!option) return

        loading.setTrue()

        if (Boolean(user.sentAt)) {
            setPendingUserPermissions()
            return
        }

        $setUserPermissions.mutate({
            uid: user.uid,
            roles: { a: option.level }
        })
    }

    const isUpgradeLockedInOrg = isRoleUpgradeDisabled && user.rolesNumber === ROLE.VIEWER

    return (
        <Dialog fullWidth maxWidth="sm" onClose={onClose} open={true}>
            {loading.isTrue && <LinearProgress />}
            <DialogTitle>Set Role</DialogTitle>
            <Grid
                container
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                style={{ height: 170 }}
            >
                {ROLES_LIST.map(o => {
                    const isUserDowngraded =
                        user.viewerOnlyAfter && user.viewerOnlyAfter.toMillis() < Date.now()
                    const roleChangeDisabled =
                        (isUserDowngraded || isUpgradeLockedInOrg) && o.level !== ROLE.VIEWER

                    return (
                        <Grid item key={o.name}>
                            <Tooltip
                                title={
                                    roleChangeDisabled
                                        ? `You have used up all seats on your plan. To update this user, free up a seat.`
                                        : ``
                                }
                                classes={{ popper: classes.popper }}
                            >
                                <FormControlLabel
                                    labelPlacement="bottom"
                                    label={o.name}
                                    disabled={roleChangeDisabled}
                                    control={
                                        <Radio
                                            checked={option?.name === o.name}
                                            onChange={() => setOption(o)}
                                            value={o.name}
                                            name={o.name}
                                        />
                                    }
                                />
                            </Tooltip>
                        </Grid>
                    )
                })}
            </Grid>
            <DialogActions>
                <Button onClick={onClose} color="default">
                    Cancel
                </Button>
                <Button disabled={loading.isTrue} onClick={handleSave}>
                    Set
                </Button>
            </DialogActions>
        </Dialog>
    )
}
